import React, { useState, useEffect } from 'react';

const VoiceAssistant: React.FC = () => {
  const [wakeWordEnabled, setWakeWordEnabled] = useState(() => {
    return localStorage.getItem('complic_wake_word_enabled') === 'true';
  });

  const [wakeWord, setWakeWord] = useState(() => {
    return localStorage.getItem('complic_wake_word') || 'hej komplic';
  });

  const handleWakeWordToggle = (enabled: boolean) => {
    setWakeWordEnabled(enabled);
    localStorage.setItem('complic_wake_word_enabled', enabled.toString());

    // Dispatch storage event to notify other components
    window.dispatchEvent(new Event('storage'));
  };

  const handleWakeWordChange = (word: string) => {
    setWakeWord(word);
    localStorage.setItem('complic_wake_word', word);

    // Dispatch storage event to notify other components
    window.dispatchEvent(new Event('storage'));
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 font-sans normal-case">
        Voice Assistant
      </h2>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 font-sans normal-case">
          Wake Word Detection
        </h3>

        <div className="space-y-4">
          <label className="flex items-start space-x-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition">
            <input
              type="checkbox"
              checked={wakeWordEnabled}
              onChange={(e) => handleWakeWordToggle(e.target.checked)}
              className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-800 dark:text-white font-sans normal-case">
                Enable Wake Word Detection
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Always listen for wake words to activate voice input hands-free
              </p>
            </div>
          </label>

          {wakeWordEnabled && (
            <>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-sans normal-case">
                  Wake Word
                </label>
                <input
                  type="text"
                  value={wakeWord}
                  onChange={(e) => handleWakeWordChange(e.target.value)}
                  placeholder="e.g., hej komplic"
                  className="w-full px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter the phrase you want to use to activate the assistant (case insensitive)
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1 font-sans normal-case">
                      Current Wake Word
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Say <strong>"{wakeWord}"</strong> to activate the voice assistant
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-1 font-sans normal-case">
                      How It Works
                    </h4>
                    <ul className="text-sm text-green-700 dark:text-green-400 space-y-1">
                      <li>• Wake word detection runs in the background</li>
                      <li>• No external API keys required</li>
                      <li>• Works with Web Speech API (built into browser)</li>
                      <li>• Supports Czech language natively</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1 font-sans normal-case">
                      Important Notes
                    </h4>
                    <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                      <li>• Requires microphone access permission</li>
                      <li>• Works best in quiet environments</li>
                      <li>• Browser must support Web Speech API (Chrome, Edge recommended)</li>
                      <li>• May not work in private/incognito mode</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Voice Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 font-sans normal-case">
          Voice Output
        </h3>
        
        <div className="space-y-3">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Voice settings (language, voice selection) are configured directly in the Complic Chat window.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1 font-sans normal-case">
                  Available TTS Providers
                </h4>
                <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                  <li>• <strong>Azure Neural TTS</strong> - Premium quality (0.5M chars/month free) ⭐</li>
                  <li>• <strong>Google Cloud TTS</strong> - High quality (4M chars/month free)</li>
                  <li>• <strong>Browser TTS</strong> - Built-in (unlimited, basic quality)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAssistant;
